import { Component } from '@theme/component';
import { fetchReviews, fillRatingDots, formatScore, getLocale } from '@theme/product-reviews';

/**
 * @typedef {Object} ProductReviewsSummaryRefs
 * @property {HTMLElement} count
 * @property {HTMLElement} label
 */

/**
 * Shows a product's Yotpo rating once it has reviews: five dots, each filled by its share of the average score, and the
 * review count. It stays hidden while the product has none or the request fails.
 *
 * @extends {Component<ProductReviewsSummaryRefs>}
 */
class ProductReviewsSummary extends Component {
  requiredRefs = ['count', 'label'];

  connectedCallback() {
    super.connectedCallback();
    this.#render();
  }

  /**
   * Opens the section's reviews dialog while it lists this product. Quick add brings another product's rating onto a
   * page whose dialog lists the page's own product.
   */
  openDialog = () => {
    const dialog = /** @type {(HTMLElement & { showDialog?: () => void }) | null} */ (
      document.getElementById(this.dataset.dialogId ?? '')
    );
    if (dialog?.dataset.productId !== this.dataset.productId) return;

    dialog?.showDialog?.();
  };

  async #render() {
    const { appKey, productId } = this.dataset;
    if (!appKey || !productId) return;

    let page;
    try {
      page = await fetchReviews(appKey, productId);
    } catch (error) {
      console.warn('[product-reviews-summary] Reviews request failed:', error);
      return;
    }

    const { score, total } = page;
    if (!this.isConnected || total === 0) return;

    const { count, label } = this.refs;
    const formattedTotal = total.toLocaleString(getLocale());
    const dots = this.querySelector('[data-rating-dots]');

    if (dots) fillRatingDots(dots, score);
    count.textContent = formattedTotal;
    label.textContent = (this.dataset.label ?? '')
      .replace('[score]', formatScore(score))
      .replace('[count]', formattedTotal);

    this.hidden = false;
  }
}

if (!customElements.get('product-reviews-summary')) {
  customElements.define('product-reviews-summary', ProductReviewsSummary);
}
